import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  CircleNotch,
  EnvelopeSimple,
  WarningCircle,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";

import { unsubscribeExternal } from "@/client/services/campaigns/campaigns";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PAGE PUBLIQUE DE DÉSABONNEMENT (/unsubscribe?c=<id>&t=<token>)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Lien présent dans le pied de chaque e-mail de campagne adressé à un contact
 *  externe. Le token est un HMAC signé par le serveur — impossible à forger.
 *
 *  UX volontairement en deux temps (pas d'auto-confirmation au montage) :
 *  les scanners d'e-mails ouverts côté serveur ne peuvent pas déclencher un
 *  désabonnement par simple préchargement du lien.
 * ─────────────────────────────────────────────────────────────────────────────
 */

type Phase = "confirm" | "submitting" | "done" | "error";

const ease = [0.22, 1, 0.36, 1] as const;
const fadeUp = (i: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.06, duration: 0.4, ease },
});

export const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const contactId = searchParams.get("c") ?? "";
  const token = searchParams.get("t") ?? "";

  const [phase, setPhase] = useState<Phase>("confirm");
  const linkValid = Boolean(contactId && token);

  const onConfirm = async () => {
    setPhase("submitting");
    try {
      await unsubscribeExternal(contactId, token);
      setPhase("done");
    } catch {
      setPhase("error");
    }
  };

  return (
    <>
      <Helmet>
        <title>{phase === "done" ? "Désabonnement confirmé" : "Se désabonner"} — Mon CV Pro</title>
      </Helmet>

      {/* Eyebrow */}
      <motion.p
        {...fadeUp(0)}
        className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.2em] text-[#3ecf8e]"
      >
        MON CV PRO
      </motion.p>

      {/* Titre */}
      <motion.h1
        {...fadeUp(1)}
        className="mb-1 text-[1.85rem] font-extrabold leading-[1.18] tracking-[-0.025em] text-gray-900"
      >
        {phase === "done" ? "C'est noté" : "Se désabonner"}
      </motion.h1>

      {/* Sous-titre */}
      <motion.p {...fadeUp(2)} className="mb-6 text-[0.84rem] leading-relaxed text-gray-400">
        {phase === "done"
          ? "Vous ne recevrez plus d'e-mails de nos campagnes. Votre compte reste accessible si vous souhaitez revenir."
          : "Vous ne souhaitez plus recevoir nos e-mails de communication ? Confirmez ci-dessous, nous ne vous écrirons plus."}
      </motion.p>

      {/* Lien incomplet */}
      {!linkValid && (
        <motion.div {...fadeUp(3)} className="flex flex-col items-center gap-5 py-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-400/15">
            <WarningCircle size={34} weight="fill" className="text-amber-500" />
          </div>
          <div className="space-y-1.5">
            <h2 className="text-[1.35rem] font-extrabold tracking-[-0.02em] text-gray-900">
              Lien incomplet
            </h2>
            <p className="text-[0.85rem] leading-relaxed text-gray-400">
              Ouvrez le lien de désabonnement présent au bas de l'e-mail reçu pour effectuer cette
              action en un clic.
            </p>
          </div>
        </motion.div>
      )}

      {/* Succès */}
      {linkValid && phase === "done" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease }}
          className="flex flex-col items-center gap-5 py-4 text-center"
        >
          <div className="bg-[#3ecf8e]/12 flex h-16 w-16 items-center justify-center rounded-full">
            <CheckCircle size={34} weight="fill" className="text-[#3ecf8e]" />
          </div>
          <p className="text-[0.85rem] leading-relaxed text-gray-400">
            Votre adresse a bien été retirée de nos campagnes. Merci de nous l'avoir indiqué.
          </p>
        </motion.div>
      )}

      {/* Erreur */}
      {linkValid && phase === "error" && (
        <motion.div {...fadeUp(3)}>
          <p className="rounded-xl border border-red-200 bg-red-50/80 px-3.5 py-2.5 text-[0.78rem] leading-relaxed text-red-600">
            Une erreur est survenue. Le lien est peut-être expiré ou déjà utilisé. Réessayez plus
            tard.
          </p>
        </motion.div>
      )}

      {/* Confirmation (double opt-out volontaire) */}
      {linkValid && (phase === "confirm" || phase === "submitting") && (
        <motion.div {...fadeUp(3)} className="flex flex-col gap-4">
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50/60 px-3.5 py-3">
            <EnvelopeSimple size={18} className="shrink-0 text-gray-400" />
            <span className="text-[0.78rem] leading-relaxed text-gray-500">
              Je confirme ne plus vouloir recevoir d'e-mails de campagne de Mon CV Pro.
            </span>
          </div>

          <motion.div whileHover={{ scale: 1.012 }} whileTap={{ scale: 0.988 }}>
            <Button
              type="button"
              disabled={phase === "submitting"}
              onClick={() => void onConfirm()}
              className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#472cb3] text-[0.92rem] font-semibold text-white shadow-[0_4px_16px_rgba(71,44,179,0.3)] transition-all duration-200 hover:bg-[#3a22a0]"
            >
              {phase === "submitting" ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  Désabonnement…
                </>
              ) : (
                <>
                  Confirmer mon désabonnement
                  <ArrowRight
                    size={16}
                    weight="bold"
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </>
              )}
            </Button>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};
